import type { ReactElement } from 'react'
import React from 'react'
import { useMediaQuery } from 'react-responsive'
import { twMerge } from 'tailwind-merge'

import type { RadioCardImageValue } from '@ui-kit/components/formComponents/RadioCardSet/types'
import { SCREEN_SIZE } from '@ui-kit/constants'
import { body2, caption } from '@ui-kit/core/typography'

import { withInvertedIcon } from './invertedIcon'
import * as styles from './styles'
import type { WebProps } from './types'
import { useTextProps } from './useTextProps'

const RadioCardImage = (props: { image?: RadioCardImageValue }) => {
  if (typeof props.image !== 'string') {
    return props.image ?? null
  }
  return (
    <div className={styles.radioCardImage()}>
      <img src={props.image} alt="" className="size-full object-cover" />
    </div>
  )
}

const RadioCard = (props: WebProps): ReactElement => {
  const onClick = () => {
    if (props.disabled) {
      return
    }
    props.onClick(props.value)
  }

  const layout = props.layout ?? 'compact'

  const isSmallDevice = useMediaQuery({
    query: `(max-width: ${layout === 'compact' ? SCREEN_SIZE.S : SCREEN_SIZE.M}px)`,
  })

  const { getTitleProps, getDescriptionsProps, getSubtitleProps } = useTextProps({
    layout,
    variant: props.variant,
    isSmallDevice,
    isSelected: props.isSelected,
    disabled: props.disabled,
  })
  const { container, card, iconContainer, textContainer, titleContainer, tag } = styles.radioCard({
    hasTopTag: props.topTag !== undefined,
    hasBottomTag: props.bottomTag !== undefined,
    isSelected: props.isSelected,
    layout,
    variant: props.variant,
    disabled: props.disabled,
    isUntouched: props.isUntouched,
    hasTopTags: props.hasTopTags,
    hasBottomTags: props.hasBottomTags,
    hasImage: props.image !== undefined,
  })

  return (
    <div
      className={container()}
      data-testid={props.testID}
      id={`radio-cardset-button-${String(props.value)}`}
      onClick={() => true}
    >
      {props.topTag !== undefined && (
        <div
          className={twMerge(
            caption({
              align: 'center',
              className: twMerge('line-clamp-1', props.topTag.onClick !== undefined && 'underline'),
            }),
            tag({
              className: 'rounded-t-lg',
            })
          )}
          onClick={props.topTag.onClick}
        >
          {props.topTag.label}
        </div>
      )}
      <div className={card()} onClick={onClick}>
        <div className={iconContainer()}>{props.icon}</div>
        <div className={textContainer()}>
          <div className={titleContainer()}>
            {layout === 'compact' && !isSmallDevice && (
              <>
                <p className={caption(getTitleProps())}>{props.title}</p>
                {props.subtitle && <p className={caption(getSubtitleProps())}>{props.subtitle}</p>}
              </>
            )}
            {(layout === 'wide' || isSmallDevice) && (
              <>
                <p className={body2(getTitleProps())}>{props.title}</p>
                {props.subtitle && <p className={body2(getSubtitleProps())}>{props.subtitle}</p>}
              </>
            )}
          </div>
          {props.description && (
            <p className={caption(getDescriptionsProps())}>{props.description}</p>
          )}
        </div>
        <RadioCardImage image={withInvertedIcon(props.image, props.variant, props.isSelected)} />
      </div>
      {props.bottomTag !== undefined && (
        <div
          className={twMerge(
            caption({
              align: 'center',
              className: twMerge(
                'line-clamp-1',
                props.bottomTag.onClick !== undefined && 'underline'
              ),
            }),
            tag({
              className: 'rounded-b-lg',
            })
          )}
          onClick={props.bottomTag.onClick}
        >
          {props.bottomTag.label}
        </div>
      )}
    </div>
  )
}

export default RadioCard
